import { NavLink, Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useFiscalStore } from "@/store/fiscal-store";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FileBox, Upload, LayoutDashboard, FileText, ShieldCheck,
  LogOut, FileBarChart2, Sun, Moon, Package, TrendingUp, Tag, Download,
  Settings, Users, BookOpen, FileSearch, Menu, User, Eye, EyeOff, RefreshCw, Database, Sparkles,
  Calendar, Clock, CreditCard
} from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MOTIVATIONAL_QUOTES } from "@/lib/quotes";
import { DPO_INFO } from "@/lib/security";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LegalConsentModal } from "@/components/LegalConsentModal";
import { Calendar as CalendarUI } from "@/components/ui/calendar";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

import { ptBR } from "date-fns/locale";

const SectionTitle = ({ children, sectionTitle }: { children: React.ReactNode, sectionTitle: string }) => (
  <div className={cn("px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-widest font-semibold", sectionTitle)}>
    {children}
  </div>
);

const NavItem = ({
  to, label, icon: Icon, badge, tag, disabled, isActive, linkActive, linkIdle, setIsMobileMenuOpen
}: {
  to: string; label: string; icon: any; badge?: number; tag?: string; disabled?: boolean; isActive: boolean; linkActive: string; linkIdle: string; setIsMobileMenuOpen: (v: boolean) => void;
}) => {
  return (
    <NavLink
      to={to}
      onClick={(e) => { 
        if (disabled) e.preventDefault();
        else setIsMobileMenuOpen(false);
      }}
      className={cn(
        "relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
        isActive ? linkActive : linkIdle,
        disabled && "opacity-35 cursor-not-allowed hover:bg-transparent",
      )}
    >
      <Icon className="size-4 shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="size-5 rounded-full text-[10px] font-bold grid place-items-center bg-orange-500 text-white leading-none shrink-0">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {tag && (
        <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold shrink-0 bg-sky-500/20 text-sky-400">
          {tag}
        </span>
      )}
    </NavLink>
  );
};

const SidebarContent = ({ 
  mobile = false, sidebarStyle, dividerCls, isDark, hasData, isAdmin, location, linkActive, linkIdle, sectionTitle, setIsMobileMenuOpen, sidebarInfo, textSubtle 
}: any) => (
  <div className={cn(
    "flex flex-col h-full overflow-hidden",
    mobile && "rounded-r-3xl"
  )} style={mobile ? sidebarStyle : undefined}>
    {/* Logo */}
    <div className={cn("px-6 py-6 border-b", dividerCls)}>
      <Link to="/upload" className="flex items-center gap-4 group transition-transform active:scale-95">
        <div className="size-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all">
          <FileBox className="size-6 text-white" />
        </div>
        <div className="leading-tight">
          <div className={cn("font-bold text-base tracking-tight", isDark || mobile ? "text-white" : "text-slate-900")}>Extrator Fiscal</div>
          <div className={cn("text-xs font-medium opacity-60", isDark || mobile ? "text-slate-300" : "text-slate-600")}>NF-E / NFC-E</div>
        </div>
      </Link>
    </div>

    {/* Nav scrollable */}
    <nav className="p-4 flex-1 overflow-y-auto space-y-1 [scrollbar-width:thin]">
      <SectionTitle sectionTitle={sectionTitle}>Operação</SectionTitle>
      <NavItem to="/upload" label="Upload XML" icon={Upload} isActive={location.pathname === "/upload"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      <SectionTitle sectionTitle={sectionTitle}>Visão Geral</SectionTitle>
      <NavItem to="/dashboard" label="Dashboard" icon={LayoutDashboard} disabled={!hasData} isActive={location.pathname === "/dashboard"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      <SectionTitle sectionTitle={sectionTitle}>Análise Fiscal</SectionTitle>
      <NavItem to="/notas" label="Notas" icon={FileText} disabled={!hasData} isActive={location.pathname === "/notas"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <NavItem to="/produtos" label="Análise de Produtos" icon={Package} disabled={!hasData} isActive={location.pathname === "/produtos"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <NavItem to="/tributacao" label="Tributação" icon={TrendingUp} disabled={!hasData} isActive={location.pathname === "/tributacao"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <NavItem to="/classificacao" label="Classificação Fiscal" icon={Tag} tag="NCM/CFOP" disabled={!hasData} isActive={location.pathname === "/classificacao"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      <SectionTitle sectionTitle={sectionTitle}>Inspeção e Indícios</SectionTitle>
      <NavItem to="/inspecao" label="Inspeção de Malha" icon={ShieldCheck} disabled={!hasData} isActive={location.pathname === "/inspecao"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <NavItem to="/validacao-xml" label="Validação de XML" icon={FileSearch} disabled={!hasData} isActive={location.pathname === "/validacao-xml"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      <SectionTitle sectionTitle={sectionTitle}>Relatórios</SectionTitle>
      <NavItem to="/relatorio" label="Relatórios" icon={FileBarChart2} disabled={!hasData} isActive={location.pathname === "/relatorio"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
      <NavItem to="/exportacoes" label="Exportações" icon={Download} tag="PDF, Excel" disabled={!hasData} isActive={location.pathname === "/exportacoes"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      <SectionTitle sectionTitle={sectionTitle}>Assinatura</SectionTitle>
      <NavItem to="/minha-assinatura" label="Minha Assinatura" icon={CreditCard} isActive={location.pathname === "/minha-assinatura"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />

      {isAdmin && (
        <>
          <SectionTitle sectionTitle={sectionTitle}>Configurações</SectionTitle>
          <NavItem to="/admin" label="Administração" icon={ShieldCheck} tag="ADM" isActive={location.pathname === "/admin"} linkActive={linkActive} linkIdle={linkIdle} setIsMobileMenuOpen={setIsMobileMenuOpen} />
        </>
      )}
    </nav>

    {/* Footer - REDIMENSIONADO (MENOR) */}
    <div className={cn("px-4 py-4 border-t space-y-3", dividerCls)}>
      <div className={cn("px-2 space-y-1", textSubtle)}>
        <div className="text-[9px] font-black uppercase tracking-[0.2em] opacity-30">Infraestrutura</div>
        <div className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{sidebarInfo.version}</div>
        <div className="text-[9px] leading-tight opacity-50 flex flex-col">
          <span>DPO: {sidebarInfo.dpo_name}</span>
          <a href={`mailto:${sidebarInfo.dpo_email}`} className="underline hover:no-underline hover:text-primary transition-colors">
            {sidebarInfo.dpo_email}
          </a>
        </div>
        <div className="text-[8px] leading-[1.3] text-amber-600/70 dark:text-amber-500/50 font-medium mt-2 pt-2 border-t border-slate-100 dark:border-white/5 italic">
          {sidebarInfo.warning}
        </div>
      </div>
    </div>
  </div>
);

function ClockComponent({ isDark }: { isDark: boolean }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const formatarData = (d: Date) => {
    const meses = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    return `${String(d.getDate()).padStart(2, "0")} de ${meses[d.getMonth()]} de ${d.getFullYear()}`;
  };

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className={cn(
          "h-11 flex items-center gap-4 px-5 rounded-xl transition-all duration-300 shadow-sm border shrink-0 cursor-help group",
          isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white/70 border-white/80 hover:bg-white/90"
        )} style={{ backdropFilter: "blur(16px)" }}>
          <div className="flex items-center gap-2">
            <Calendar className={cn("size-4 transition-colors", isDark ? "text-white/40" : "text-slate-400 group-hover:text-blue-600")} />
            <span className={cn("text-xs font-black tracking-tight", isDark ? "text-white" : "text-slate-900")}>
              {formatarData(now)}
            </span>
          </div>
          
          <div className={cn("h-4 w-px", isDark ? "bg-white/10" : "bg-slate-200")} />
          
          <div className="flex items-center gap-2">
            <Clock className={cn("size-4 transition-colors", isDark ? "text-white/40" : "text-slate-400")} />
            <span className="text-xs font-mono font-black text-[#1A66CC] dark:text-[#33CCFF] leading-none tabular-nums">
              {now.toLocaleTimeString("pt-BR")}
            </span>
          </div>
        </div>
      </HoverCardTrigger>
      <HoverCardContent 
        side="bottom" 
        align="start" 
        sideOffset={10}
        className={cn(
          "w-auto p-0 border-none bg-transparent shadow-2xl z-[9999]",
          "animate-in fade-in zoom-in-95 duration-200"
        )}
      >
        <div className={cn(
          "relative overflow-hidden rounded-2xl border p-4 backdrop-blur-2xl transition-all",
          isDark 
            ? "bg-[#0A1229]/95 border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.5)]" 
            : "bg-white/95 border-slate-200 shadow-[0_20px_50px_rgba(0,0,0,0.1)]"
        )}>
          <CalendarUI
            mode="single"
            selected={now}
            locale={ptBR}
            className={cn(
              "rounded-md border-none p-2",
              isDark ? "text-white" : "text-slate-900"
            )}
            classNames={{
              month: "space-y-4",
              caption: "flex justify-center pt-1 relative items-center px-8",
              caption_label: cn("text-sm font-black", isDark ? "text-white" : "text-slate-900"),
              nav: "space-x-1 flex items-center",
              nav_button: cn(
                "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 transition-opacity",
                isDark ? "text-white hover:bg-white/10" : "text-slate-900 hover:bg-slate-100"
              ),
              table: "w-full border-collapse space-y-1",
              head_row: "flex w-full justify-between",
              head_cell: cn(
                "text-slate-500 rounded-md w-9 font-black text-[10px] uppercase tracking-widest",
                isDark ? "text-white/40" : "text-slate-400"
              ),
              row: "flex w-full mt-2 justify-between",
              cell: "text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
              day: cn(
                "h-9 w-9 p-0 font-black aria-selected:opacity-100 rounded-lg transition-all flex items-center justify-center",
                isDark 
                  ? "text-white/70 hover:bg-white/10 hover:text-white" 
                  : "text-slate-600 hover:bg-blue-50 hover:text-blue-600"
              ),
              day_selected: cn(
                "bg-blue-600 text-white !opacity-100 hover:bg-blue-700 hover:text-white focus:bg-blue-600 focus:text-white shadow-[0_5px_15px_rgba(37,99,235,0.4)]",
                "font-black"
              ),
              day_today: cn(
                "border-2 border-blue-500/30", 
                isDark ? "text-blue-400" : "text-blue-600"
              ),
              day_outside: "text-slate-500 opacity-20",
              day_disabled: "text-slate-500 opacity-20",
              day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
          />
          <div className={cn(
            "mt-3 pt-3 border-t text-[10px] font-black uppercase tracking-widest text-center",
            isDark ? "border-white/10 text-white/20" : "border-slate-100 text-slate-400"
          )}>
            Calendário Operacional
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function AppShell() {
  const [sidebarInfo, setSidebarInfo] = useState({
    version: "v2.4.0 · SPED Fiscal 2026",
    dpo_name: "Encarregado de Dados (DPO)",
    dpo_email: "privacidade@extratorfiscal.app",
    warning: "Atenção: Ferramenta auxiliar. A tomada de decisões tributárias exige a validação por um Profissional Contábil (CRC)."
  });

  useEffect(() => {
    async function loadSidebarInfo() {
      const { data } = await supabase.from("app_settings").select("*").eq("key", "sidebar_info").maybeSingle();
      if (data?.value) {
        try {
          setSidebarInfo(JSON.parse(data.value));
        } catch (e) {
          console.error("Erro ao parsear sidebar_info", e);
        }
      }
    }
    loadSidebarInfo();
  }, []);

  const dashboard = useFiscalStore((s) => s.dashboard);
  const hasData = !!dashboard;
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isAdmin } = useAuth();
  const notas = useFiscalStore((s) => s.notas);
  const totalNotas = dashboard?.stats.totalNotas || notas.length;
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(MOTIVATIONAL_QUOTES[0]);

  useEffect(() => {
    const updateQuote = () => {
      const idx = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
      setCurrentQuote(MOTIVATIONAL_QUOTES[idx]);
    };
    updateQuote();
    const interval = setInterval(updateQuote, 2 * 60 * 60 * 1000); // 2 horas
    return () => clearInterval(interval);
  }, []);

  const privacyMode = useFiscalStore((s) => s.privacyMode);
  const togglePrivacyMode = useFiscalStore((s) => s.togglePrivacyMode);
  const purgeAllData = useFiscalStore((s) => s.purgeAllData);

  async function handleLogout() {
    purgeAllData();
    await signOut();
    navigate("/login", { replace: true });
  }

  const handleNewSession = () => {
    if (confirm("Deseja iniciar uma nova sessão? Todos os dados atuais serão removidos.")) {
      purgeAllData();
      navigate("/upload", { replace: true });
    }
  };

  const getUserDisplayName = () => {
    if (!user) return "Usuário";
    const name = user.user_metadata?.full_name || user.email?.split("@")[0] || "Usuário";
    const firstName = name.split(/[._ ]/)[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };

  const sidebarStyle = isDark
    ? {
        background: "linear-gradient(180deg, rgba(15,35,95,0.82), rgba(8,20,60,0.86))",
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        border: "1px solid rgba(120,180,255,0.22)",
        boxShadow: "0 8px 32px rgba(0,10,40,0.45)",
      }
    : {
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(20px) saturate(140%)",
        WebkitBackdropFilter: "blur(20px) saturate(140%)",
        border: "1px solid rgba(255,255,255,0.7)",
        boxShadow: "0 8px 32px rgba(20,60,140,0.18)",
      };

  const textBase = isDark ? "text-slate-100" : "text-slate-800";
  const textMuted = isDark ? "text-slate-300/80" : "text-slate-600";
  const textSubtle = isDark ? "text-slate-400/80" : "text-slate-500";
  const sectionTitle = isDark ? "text-slate-400/70" : "text-slate-500";
  const dividerCls = isDark ? "border-white/10" : "border-slate-900/10";
  const linkIdle = isDark
    ? "text-slate-200 hover:bg-white/5 hover:text-white"
    : "text-slate-700 hover:bg-slate-900/5 hover:text-slate-900";
  const linkActive = isDark
    ? "bg-[linear-gradient(135deg,rgba(51,153,255,0.28),rgba(51,204,255,0.14))] text-white shadow-[inset_3px_0_0_0_#33CCFF,0_4px_18px_rgba(51,153,255,0.25)]"
    : "bg-[linear-gradient(135deg,rgba(51,153,255,0.18),rgba(51,204,255,0.10))] text-slate-900 shadow-[inset_3px_0_0_0_#1A66CC,0_4px_18px_rgba(51,153,255,0.18)]";

  return (
    <div className={cn("h-screen overflow-hidden flex flex-col lg:flex-row", textBase)}>
      <LegalConsentModal />
      {/* Sidebar Desktop */}
      <aside className={cn("hidden lg:flex w-64 shrink-0 m-3 mr-0 rounded-2xl flex-col overflow-hidden")} style={sidebarStyle}>
        <SidebarContent 
          sidebarStyle={sidebarStyle} 
          dividerCls={dividerCls} 
          isDark={isDark} 
          hasData={hasData} 
          isAdmin={isAdmin} 
          location={location} 
          linkActive={linkActive} 
          linkIdle={linkIdle} 
          sectionTitle={sectionTitle} 
          setIsMobileMenuOpen={setIsMobileMenuOpen} 
          sidebarInfo={sidebarInfo} 
          textSubtle={textSubtle}
        />
      </aside>

      {/* Mobile Top Header — Premium Glass */}
      <header className={cn(
        "lg:hidden h-16 shrink-0 flex items-center justify-between px-4 z-50 transition-all duration-300",
        isDark ? "bg-[#0A1A4A]/80 border-b border-white/10" : "bg-white/80 border-b border-slate-200"
      )} style={{ backdropFilter: "blur(20px)" }}>
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-xl bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center shadow-lg shadow-blue-500/30">
            <FileBox className="size-4.5 text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-bold text-sm tracking-tight">Extrator Fiscal</span>
            <span className="text-[9px] font-medium opacity-50 uppercase tracking-widest">Inspeção</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-xl hover:bg-primary/10 hover:text-primary">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 border-none w-72">
              <SidebarContent 
                mobile 
                sidebarStyle={sidebarStyle} 
                dividerCls={dividerCls} 
                isDark={isDark} 
                hasData={hasData} 
                isAdmin={isAdmin} 
                location={location} 
                linkActive={linkActive} 
                linkIdle={linkIdle} 
                sectionTitle={sectionTitle} 
                setIsMobileMenuOpen={setIsMobileMenuOpen} 
                sidebarInfo={sidebarInfo} 
                textSubtle={textSubtle}
              />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1 flex flex-col min-w-0 h-full relative overflow-hidden">
        {/* Global Header — Adaptive Style */}
        <header className="h-16 lg:h-20 shrink-0 flex items-center px-4 lg:px-8 z-10 gap-2 overflow-hidden">
          <div className="shrink-0">
            <ClockComponent isDark={isDark} />
          </div>

          <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
            {/* Session Group - Visible from 2xl */}
            <div className={cn(
              "hidden 2xl:flex h-11 items-center gap-2.5 px-3.5 rounded-xl border transition-all duration-300 shadow-sm shrink-0 min-w-[120px]",
              isDark ? "bg-white/5 border-white/10" : "bg-white/70 border-white/80"
            )}>
              <div className="size-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 shadow-inner shrink-0">
                <Database className="size-4" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[8px] font-black uppercase tracking-widest opacity-40">Sessão</span>
                <span className="text-xs font-black tabular-nums">{totalNotas.toLocaleString("pt-BR")} <span className="opacity-40 text-[9px]">Notas</span></span>
              </div>
            </div>

            <button
              onClick={handleNewSession}
              title="Iniciar Nova Sessão"
              className={cn(
                "hidden lg:flex h-11 px-4 rounded-xl items-center gap-2 font-black text-[11px] uppercase tracking-widest transition-all shadow-sm border shrink-0",
                isDark 
                  ? "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white" 
                  : "bg-white/70 border-white/80 text-slate-600 hover:bg-white/90"
              )}
            >
              <RefreshCw className="size-4" />
              Reset
            </button>

            {/* Motivational Quote - Full Text Tooltip */}
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <div className={cn(
                    "hidden xl:flex h-11 items-center gap-3 px-5 rounded-xl border transition-all duration-300 max-w-[750px] flex-1 group relative overflow-hidden min-w-0 shadow-sm cursor-help",
                    isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white/70 border-white/80 hover:bg-white/90"
                  )}>
                    <Sparkles className="size-4 text-amber-500 shrink-0 animate-pulse" />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-[11px] font-medium italic truncate opacity-80 leading-tight">"{currentQuote.text}"</div>
                      <div className="text-[9px] font-black uppercase tracking-tighter opacity-40">— {currentQuote.author}</div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="bottom" 
                  sideOffset={8}
                  className="max-w-[320px] p-0 border-none bg-transparent shadow-2xl z-[9999]"
                >
                  <div className={cn(
                    "relative overflow-hidden rounded-xl border p-4 backdrop-blur-xl transition-all duration-300",
                    isDark 
                      ? "bg-[#0A1229]/90 border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.5)]" 
                      : "bg-white/90 border-slate-200 shadow-[0_10px_30px_rgba(0,0,0,0.1)]"
                  )}>
                    <div className="relative z-10 flex flex-col gap-2">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="size-3 text-blue-500 animate-pulse" />
                        <span className={cn(
                          "text-[9px] font-black uppercase tracking-widest",
                          isDark ? "text-blue-400/80" : "text-blue-600/80"
                        )}>Citação</span>
                      </div>
                      
                      <p className={cn(
                        "text-[12px] font-medium leading-snug italic",
                        isDark ? "text-white/90" : "text-slate-800"
                      )}>
                        "{currentQuote.text}"
                      </p>
                      
                      <div className={cn(
                        "text-[10px] font-black uppercase tracking-tighter mt-1 pt-2 border-t",
                        isDark ? "text-white/30 border-white/5" : "text-slate-400 border-slate-100"
                      )}>
                        — {currentQuote.author}
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* User Greeting */}
            {user && (
              <div className={cn(
                "hidden md:flex h-11 items-center gap-2.5 pl-2 pr-4 rounded-xl border transition-all duration-300 shadow-sm shrink-0 min-w-[140px]",
                isDark ? "bg-white/5 border-white/10" : "bg-white/70 border-white/80"
              )}>
                <div className="size-8 rounded-lg bg-gradient-to-br from-[#33CCFF] to-[#1A66CC] grid place-items-center shadow-md">
                  <User className="size-4 text-white" />
                </div>
                <div className="hidden lg:flex flex-col leading-none">
                  <span className="text-[9px] font-medium opacity-50">Logado</span>
                  <span className={cn("text-xs font-black tracking-tight max-w-[80px] truncate", isDark ? "text-white" : "text-slate-900")}>
                    {getUserDisplayName()}
                  </span>
                </div>
              </div>
            )}

            {/* Tool Group */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={togglePrivacyMode}
                title={privacyMode ? "DLP Ativo" : "DLP Inativo"}
                className={cn(
                  "size-11 rounded-xl grid place-items-center border transition-all duration-300 shadow-sm",
                  isDark ? "bg-white/5 border-white/10" : "bg-white/70 border-white/80",
                  privacyMode ? "text-emerald-500 bg-emerald-500/5 border-emerald-500/20" : (isDark ? "text-slate-400" : "text-slate-600")
                )}
              >
                {privacyMode ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
              </button>

              <button
                type="button"
                onClick={toggle}
                className={cn(
                  "size-11 rounded-xl grid place-items-center border transition-all duration-300 shadow-sm",
                  isDark ? "bg-white/5 border-white/10 text-yellow-400" : "bg-white/70 border-white/80 text-slate-600"
                )}
              >
                {isDark ? <Sun className="size-5" /> : <Moon className="size-5" />}
              </button>

              <button
                onClick={handleLogout}
                className={cn(
                  "h-11 px-4 rounded-xl flex items-center gap-2 font-black text-[11px] uppercase tracking-widest transition-all shadow-sm border",
                  isDark 
                    ? "bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20" 
                    : "bg-white/70 border-white/80 text-slate-600 hover:text-red-600 hover:bg-red-50"
                )}
              >
                <LogOut className="size-4" />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto scroll-smooth p-4 lg:p-8 pt-0 lg:pt-2 [scrollbar-width:thin] relative">
          {privacyMode && (
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center overflow-hidden opacity-[0.03] dark:opacity-[0.04]">
              <div className="rotate-[-35deg] text-center whitespace-nowrap">
                {Array.from({ length: 20 }).map((_, i) => (
                  <div key={i} className="text-4xl lg:text-7xl font-black mb-8 select-none">
                    CONFIDENCIAL • {user?.email || "USUÁRIO"} • {new Date().toLocaleDateString("pt-BR")}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="max-w-[1600px] mx-auto pb-12 relative z-10">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
}

